import React, { useState, useCallback } from 'react';
import { Box, Typography, Avatar, Tooltip, CircularProgress } from '@mui/material';
import { NoteAlt as NoteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatWithAnnotation } from '@/types/api';
import { useChatTheme } from '../ChatThemeContext';
import { useChatFileStats } from '@/hooks/useChannelAnnotations';
import { useChatLabel } from '@/hooks/useChatLabel';
import { formatCount, formatFileSize } from '@/utils/formatters';
import DbStatusIndicator from './DbStatusIndicator';

interface ChatSidebarItemProps {
  chat: ChatWithAnnotation;
  isSelected: boolean;
  onClick: () => void;
}

const getAvatarColor = (name: string): string => {
  const colors = ['#e17076', '#7bc862', '#e5ca77', '#65aadd', '#a695e7', '#ee7aae', '#6ec9cb', '#faa774'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ChatSidebarItem: React.FC<ChatSidebarItemProps> = ({ chat, isSelected, onClick }) => {
  const { t } = useTranslation('pages');
  const { colors, mode } = useChatTheme();
  const chatLabel = useChatLabel();
  const name = chatLabel(chat);
  const firstLetter = name.charAt(0).toUpperCase();
  const hasNote = !!chat.annotation?.notes && chat.annotation.notes.trim() !== '';
  const dbStatus = chat.annotation?.db_status || 'not_in_db';

  const [hovered, setHovered] = useState(false);

  const { data: fileStats, isFetching: statsFetching } = useChatFileStats(hovered && chat.file_count > 0 ? chat.id : null);

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  const mutedColor = isSelected && mode === 'light' ? 'rgba(255,255,255,0.7)' : colors.sidebarTextMuted;

  const showLoading = !fileStats && statsFetching;

  const tooltipContent = (
    <Box sx={{ p: 0.5, minWidth: 160, direction: 'ltr', textAlign: 'left' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
        {t('chatPreview.sidebar.fileBreakdown')}
      </Typography>
      {showLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={16} color="inherit" />
        </Box>
      ) : fileStats && fileStats.categories.length > 0 ? (
        fileStats.categories
          .sort((a, b) => b.file_count - a.file_count)
          .map((cat) => (
            <Box key={cat.category} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.15 }}>
              <Typography variant="caption">{cat.category}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {formatCount(cat.file_count)} · {formatFileSize(cat.total_size)}
              </Typography>
            </Box>
          ))
      ) : (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t('chatPreview.sidebar.noFiles')}
        </Typography>
      )}
      {/* The categories come from a hand-refreshed cache and a file with an
          unknown mime type joins none of them — they do not add up to the
          channel's file count, and must not be shown as if they did. */}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
        {t('chatPreview.sidebar.fileBreakdownNote')}
      </Typography>
    </Box>
  );

  // Numbers are isolated (FSI…PDI) rather than the line being forced LTR: the
  // label beside them is Arabic and would otherwise be reordered.
  const fileInfo = chat.file_count > 0
    ? t('chatPreview.sidebar.filesSummary', {
        files: `\u2068${formatCount(chat.file_count)}\u2069`,
        size: `\u2068${formatFileSize(chat.total_file_size)}\u2069`,
      })
    : chat.chat_type || t('chatPreview.sidebar.channelFallback');

  return (
    <Tooltip title={chat.file_count > 0 ? tooltipContent : ''} placement="right" arrow>
      <Box
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          bgcolor: isSelected ? colors.sidebarItemActive : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? colors.sidebarItemActive : colors.sidebarItemHover,
          },
          transition: 'background-color 0.15s',
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: getAvatarColor(name),
            fontSize: '1.2rem',
            fontWeight: 500,
          }}
        >
          {firstLetter}
        </Avatar>

        {/* Chat info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
              <Typography
                variant="body1"
                noWrap
                sx={{
                  color: isSelected && mode === 'light' ? '#ffffff' : colors.sidebarText,
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                }}
              >
                {name}
              </Typography>
              <DbStatusIndicator status={dbStatus} size={8} />
              {hasNote && (
                <NoteIcon sx={{ color: mutedColor, fontSize: 14, flexShrink: 0 }} />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: mutedColor,
                fontSize: '0.8125rem',
              }}
            >
              {fileInfo}
            </Typography>

            {/* Message count badge */}
            {chat.message_count > 0 && (
              <Box
                sx={{
                  bgcolor: isSelected && mode === 'light' ? 'rgba(255,255,255,0.3)' : colors.sidebarBadgeBg,
                  color: colors.sidebarBadgeText,
                  borderRadius: '12px',
                  px: 0.75,
                  py: 0.125,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  minWidth: 20,
                  textAlign: 'center',
                }}
              >
                {/* Shared helper, so the badge and the file counter above it
                    round on the same thresholds. */}
                {formatCount(chat.message_count)}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default ChatSidebarItem;
